import { useTranslation } from 'react-i18next'

import { EMPTY_LIST } from '../../../../../constants/common'
import { SERVER_NON_FIELD_ERROR_KEY } from '../../../../../constants/validation'
import {
  useDateLocale,
  useNotification,
  useTimeZone,
  useUserData,
} from '../../../../../hooks'
import {
  getMockCalendarRequestsDayLoad,
  moveMockCalendarFollowUps,
} from '../../../Calendar.mock'
import {
  formatCalendarDate,
  getCalendarNextDate,
  getIsCalendarFollowUp,
} from '../../../Calendar.utils'
import {
  CalendarRelieveDayModal,
  getCalendarRelieveDayInitialValues,
} from '../../../CalendarRelieveDayModal'

import { CALENDAR_VIEW_RELIEVE_DAY_NEXT_FREE_DAY_FAILED } from './useCalendarViewRelieveDay.constants'
import {
  getCalendarViewRelieveDayCapText,
  getCalendarViewRelieveDayNextFreeDay,
} from './useCalendarViewRelieveDay.utils'

export function useCalendarViewRelieveDay({
  getActionToken,
  setModal,
  onClose,
  onReload,
}) {
  const { t } = useTranslation()

  const locale = useDateLocale()
  const timezone = useTimeZone()
  const userData = useUserData()

  const { handleAddSuccessNotification, handleAddWarningNotification } =
    useNotification()

  function handleRelieveDayDone({ data, payload }) {
    onReload()

    const title = t('Calendar.RelieveDayDoneToastKey', {
      count: payload.ids.length,
      date: formatCalendarDate({ date: payload.date, locale }),
    })

    const capText = getCalendarViewRelieveDayCapText({
      advisory: data.day_load_advisory,
      locale,
      t,
    })

    if (capText) {
      handleAddWarningNotification({ text: capText, title })
    } else {
      handleAddSuccessNotification({ title })
    }
  }

  async function handleSubmitRelieveDay({
    day,
    payload,
    setErrors,
    setSubmitting,
  }) {
    const actionToken = getActionToken()

    // TODO: move the due dates with the requests mass action in its date-only
    // mode, keeping each request's time of day, and read the day-load
    // advisory of its answer (TM-01-49, TM-01-14).
    return moveMockCalendarFollowUps({
      date: payload.date,
      ids: payload.ids,
      sourceDate: day,
      timezone,
    })
      .then(({ data }) => {
        if (getActionToken() === actionToken) {
          onClose()
        }

        handleRelieveDayDone({ data, payload })
      })
      .catch(() => {
        setSubmitting(false)

        setErrors({
          [SERVER_NON_FIELD_ERROR_KEY]: t('Error.CommonApiRequestFailKey'),
        })
      })
  }

  // TODO: read the next free day from the day-load endpoint once the backend
  // serves it (TM-01-12).
  async function readNextFreeDay(day) {
    const date = getCalendarNextDate(day)

    return getMockCalendarRequestsDayLoad({
      date,
      requestIds: EMPTY_LIST,
      timezone,
      user: userData,
    })
      .then(({ data }) =>
        getCalendarViewRelieveDayNextFreeDay({ data, date, user: userData })
      )
      .catch(() => CALENDAR_VIEW_RELIEVE_DAY_NEXT_FREE_DAY_FAILED)
  }

  function handleOpenRelieveDayModal({ day, dayLoad, items, nextFreeDay }) {
    setModal(
      <CalendarRelieveDayModal
        day={day}
        dayLoad={dayLoad}
        followUps={items.filter(getIsCalendarFollowUp)}
        initialValues={getCalendarRelieveDayInitialValues({
          nextFreeDay,
          timezone,
        })}
        nextFreeDay={nextFreeDay}
        onClose={onClose}
        onSubmit={handleSubmitRelieveDay}
      />
    )
  }

  async function handleOpenRelieveDay({ day, dayLoad, items }) {
    const actionToken = getActionToken()

    handleOpenRelieveDayModal({ day, dayLoad, items, nextFreeDay: null })

    return readNextFreeDay(day).then(nextFreeDay => {
      if (getActionToken() === actionToken) {
        handleOpenRelieveDayModal({ day, dayLoad, items, nextFreeDay })
      }
    })
  }

  return { handleOpenRelieveDay }
}
